/* ************************************************************************** */
/*                                Dependencies                                */
/* ************************************************************************** */

// Lib dependencies
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

// UI lib components
import {
  Card,
  Flex,
  Text,
  Loader,
  Stack,
  Textarea,
  ActionIcon,
  Group,
} from '@mantine/core';

// Shared components
import AssistantMessage from '@features/AIAssistant/AssistantMessage';
import ThinkingFeed from '@features/AIAssistant/ThinkingFeed';

// API & Local types
import { SectionContentProps } from '@features/Resource/ResourceMenu/types';
import {
  AssistantMessageRole,
  SortDirection,
  useGetAssistantMessagesQuery,
  useGetAssistantThreadForResourceQuery,
} from '@app/types/blackroom-api';
import { Message } from './types';
import { parseSSEStream } from '@features/AIAssistant/utils/parseSSEStream';

// Store
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';
import useViewAs from '@shared/providers/ViewAsProvider/useViewAs';

// styles
import { palette } from '@app/shared/ui-kit/colors';
import * as classes from './styles/index.css';
import { useAuth0 } from '@auth0/auth0-react';
import { IconArrowUp, IconMaximize, IconMinimize } from '@tabler/icons-react';
import AIBadge from '@app/shared/ui-kit/molecules/AIBadge';

/* ************************************************************************** */
/*                                  Component                                 */
/* ************************************************************************** */

function SectionInsightsContent({
  resource,
  isFullscreen,
  onToggleFullscreen,
}: SectionContentProps) {
  /* ********************************** HOOKS ********************************* */
  const { getAccessTokenSilently } = useAuth0();
  const workspace = useWorkspace();
  // View-as shows the member's existing thread; asking would write to their account.
  const { isViewingAs } = useViewAs();

  const [input, setInput] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [messageLoading, setMessageLoading] = useState(false);
  const [summary, setSummary] = useState<string>('');
  const [loadingSummary, setLoadingSummary] = useState<boolean>(true);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const hasFetchedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isStreamingRef = useRef(false);

  const { data: threadData } = useGetAssistantThreadForResourceQuery({
    variables: { workspaceId: workspace.id, resourceId: resource.id },
  });

  const { data: messagesData, refetch: refetchMessages } =
    useGetAssistantMessagesQuery({
      variables: {
        filter: { threadId: threadData?.assistantThreadForResource?.id ?? '' },
        sorter: { createdAt: SortDirection.Desc },
      },
      skip: !threadData?.assistantThreadForResource?.id,
      fetchPolicy: 'cache-and-network',
    });

  // Localization
  const { t } = useTranslation('Insight');

  // Reset state when resource changes
  useEffect(() => {
    hasFetchedRef.current = false;
    setSummary('');
    setLoadingSummary(true);
    setMessages([]);
  }, [resource]);

  // Populate messages history
  useEffect(() => {
    if (isStreamingRef.current) return;

    if (messagesData?.assistantMessages.nodes) {
      setMessages([...messagesData.assistantMessages.nodes].reverse());
    }
  }, [messagesData]);

  // Scroll to bottom when new messages are added or we switch to fullscreen
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }, [messages, isFullscreen]);

  // Put a blur effect on the header when we scroll
  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        setIsScrolled(scrollTop > 20); // Activate blur after 20px scroll
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    // Because of StrictMode, the useEffect is called twice in dev environment, so we need to block the second call
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;

      if (resource.summary) {
        // Stream the existing summary to create the AI effect
        streamExistingSummary(resource.summary);
      } else if ('summary' in resource) {
        // Full resource loaded, confirmed no summary available
        setLoadingSummary(false);
        setSummary(t('Insight:DialogResourceInsights.summary.noSummary'));
      }
      // Otherwise keep showing loader until full resource data arrives
    }
  }, [resource]);

  useEffect(() => {
    return () => {
      // Cancel any ongoing operations when resource is about to change
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [resource]);

  /* ******************************** CALLBACKS ******************************* */

  const streamExistingSummary = async (existingSummary: string) => {
    setLoadingSummary(true);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    await new Promise((resolve) => setTimeout(resolve, 1000));

    setLoadingSummary(false);
    setSummary('');

    const words = existingSummary.split(' ');
    let currentText = '';

    for (let i = 0; i < words.length; i++) {
      // Check if streaming should be cancelled
      if (abortController.signal.aborted) {
        break;
      }

      currentText += (i > 0 ? ' ' : '') + words[i];
      setSummary(currentText);

      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  };

  const askQuestion = async () => {
    setMessageLoading(true);
    isStreamingRef.current = true;

    // Create AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const token = await getAccessTokenSilently();

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: Math.random().toString(36).slice(-6),
          role: AssistantMessageRole.User,
          createdAt: new Date(),
          content: input,
        },
      ]);

      // We empty the input after adding the content to the chat
      const inputToSend = input;
      setInput('');

      const assistantMessageId = Math.random().toString(36).slice(-6);

      setMessages((prevMessages) => [
        ...prevMessages,
        {
          id: assistantMessageId,
          role: AssistantMessageRole.Assistant,
          createdAt: new Date(),
          content: '',
          isStreaming: true,
          isThinking: true,
        },
      ]);

      const res = await fetch(
        `${import.meta.env.VITE_API_CORE_REST_URL}/openai/getAnswer`,
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: token ? `Bearer ${token}` : '',
          },
          method: 'POST',
          body: JSON.stringify({
            workspaceId: workspace.id,
            resourceId: resource.id,
            question: inputToSend,
            threadId: threadData?.assistantThreadForResource?.id ?? null,
          }),
          signal: abortController.signal,
        },
      );

      if (!res.ok || !res.body) {
        updateMessage({ isStreaming: false });
      } else {
        let accumulatedText = '';

        for await (const event of parseSSEStream(res.body)) {
          if (abortController.signal.aborted) break;

          if (event.type === 'content') {
            accumulatedText += event.content;
            updateMessage({ content: accumulatedText, isThinking: false });
          } else if (event.type === 'done') {
            updateMessage({
              isStreaming: false,
              ...(event.messageId && { id: event.messageId }),
            });
          }
        }
      }
    } catch (error) {
      // Handle abort error gracefully
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Question asking was cancelled');
      } else {
        console.error('Error asking question:', error);
      }
    } finally {
      setMessageLoading(false);
      isStreamingRef.current = false;
      if (!abortController.signal.aborted) {
        refetchMessages();
      }
      // Clean up the abort controller reference
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  };

  const updateMessage = (partial: Partial<Message>) => {
    setMessages((prevMessages) => {
      const lastElement = prevMessages.at(-1);

      if (!lastElement) return prevMessages;

      return [
        ...prevMessages.slice(0, -1),
        {
          ...lastElement,
          ...partial,
        },
      ];
    });
  };

  /* ******************************** RENDERING ******************************* */

  return (
    <Stack h="100%" justify="space-between" gap="0">
      <Group
        justify="flex-end"
        gap="0.5rem"
        className={`${classes.stickyHeader} ${isScrolled ? classes.stickyHeaderBlurred : ''}`}
      >
        {onToggleFullscreen && (
          <ActionIcon
            variant="subtle"
            size="sm"
            onClick={onToggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
          >
            {isFullscreen ? (
              <IconMinimize size={16} />
            ) : (
              <IconMaximize size={16} />
            )}
          </ActionIcon>
        )}
        <AIBadge />
      </Group>

      <Flex
        ref={scrollContainerRef}
        className={classes.scrollContainer}
        direction="column"
        gap="0.5rem"
        px="0.5rem"
      >
        <Text fw="bold">
          {t('Insight:DialogResourceInsights.summary.title')}
        </Text>

        {loadingSummary ? (
          <Loader size="xs" mt="0.5rem" mb="1rem" />
        ) : (
          <Text size="sm" mt="0.5rem" mb="1rem">
            {summary}
          </Text>
        )}

        {isFullscreen && (
          <>
            {messages.map((msg) => (
              <Flex
                gap="0.5rem"
                mb="1rem"
                w={msg.role === AssistantMessageRole.User ? '80%' : '100%'}
                style={{
                  alignSelf:
                    msg.role === AssistantMessageRole.User
                      ? 'flex-end'
                      : 'auto',
                }}
                key={msg.id}
              >
                {msg.role === AssistantMessageRole.User && (
                  <Card
                    px="1rem"
                    py="0.4rem"
                    radius="sm"
                    bg={palette.blackroomLightGray3}
                    w="100%"
                  >
                    <Text size="sm" className={classes.messageContainer}>
                      {msg.content}
                    </Text>
                  </Card>
                )}
                {msg.role === AssistantMessageRole.Assistant && (
                  <>
                    {msg.isThinking && <ThinkingFeed steps={[]} isStreaming />}
                    <AssistantMessage
                      content={<ReactMarkdown>{msg.content}</ReactMarkdown>}
                      messageId={msg.id}
                      size="sm"
                      className={classes.messageContainer}
                      isStreaming={msg.isStreaming}
                    />
                  </>
                )}
              </Flex>
            ))}
          </>
        )}
      </Flex>
      <div>
        <Textarea
          labelProps={{ fw: 'bold' }}
          classNames={{
            input: classes.input,
          }}
          value={input}
          disabled={isViewingAs}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => {
            if (!isFullscreen) {
              onToggleFullscreen?.();
            }
          }}
          placeholder={t(
            'Insight:DialogResourceInsights.chat.input.placeholder',
          )}
          rows={1}
          autosize
          maxRows={10}
          flex={1}
          rightSection={
            <ActionIcon
              onClick={askQuestion}
              disabled={isViewingAs || messageLoading || !input.trim().length}
              variant="filled"
              size="md"
              radius="md"
              bg={palette.blackroomAI}
              className={classes.customActionIcon}
            >
              <IconArrowUp size={16} />
            </ActionIcon>
          }
          rightSectionWidth={35}
        />
      </div>
    </Stack>
  );
}

export default SectionInsightsContent;
